<template>
  <div>
    <v-container fluid class="py-0">
      <v-row>
        <v-col cols="9" class="px-0">
          <v-btn v-if="btnVisibleEdit" @click="handleClickEdit" small
            ><v-icon size="12" class="mr-2 btn-icon">far fa-edit</v-icon
            >Edit</v-btn
          >
          <v-btn
            @click="handleClickCopy"
            small
            class="ml-3"
            :disabled="btnDisabledCopy"
            ><v-icon size="12" class="mr-2 btn-icon">far fa-clone</v-icon
            >Copy</v-btn
          >
        </v-col>
        <v-col cols="3" class="text-right pr-0">
          <v-btn v-if="btnVisibleSend" @click="handleClickSend" small>
            Send
          </v-btn>
        </v-col>
      </v-row>

      <v-row v-if="message">
        <v-col cols="12" class="px-0">
          <v-container fluid class="py-0 hyp-container message-container">
            <v-row dense>
              <v-col cols="12" class="mx-2 pl-2 pr-5">
                <span class="application-heading">{{ message.subject }}</span>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12" class="mx-2 pl-2 pr-5">
                {{ messageDate }} - {{ messageRecipients }}
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12" class="mx-2 pl-2 pr-5">
                <div
                  ref="bodyDiv"
                  class="message-body"
                  v-bind:class="{ messageExpanded: messageExpanded }"
                  v-html="messageBody"
                ></div>
                <div v-if="isOverflowing">
                  <a v-if="messageExpanded" @click="messageExpanded = false"
                    >show less</a
                  >
                  <a v-if="!messageExpanded" @click="messageExpanded = true"
                    >show more</a
                  >
                </div>
              </v-col>
            </v-row>
            <CommsMessageRecipientsDetails
              v-if="!isDraft && recipientDetailsUnlocked"
              :message="message"
              :groups="groups"
              :locations="locations"
            ></CommsMessageRecipientsDetails>
          </v-container>
        </v-col>
      </v-row>

      <v-row v-if="!message">
        <v-col cols="12" class="px-0">
          <v-container fluid class="py-2 hyp-container">
            No message selected
          </v-container>
        </v-col>
      </v-row>
    </v-container>

    <ConfirmationPopup
      :show="confirmationShow"
      title="Delete message"
      :text="confirmationText"
      :has-text-input="false"
      :buttonCancel="confirmationBtnCancel"
      :buttonAction="confirmationBtnAction"
    ></ConfirmationPopup>
  </div>
</template>

<script>
import lodashGet from "lodash/get";
import lodashCloneDeep from "lodash/cloneDeep";
import lodashFind from "lodash/find";
import lodashUpperFirst from "lodash/upperFirst";

import {
  getMessage,
  deleteMessage,
} from "@/services/communication/communication.service";
import { getRelativeTimeFromNow } from "@/common/date.utilities";

import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";

const EMITTED_ACTIONS = {
  DELETE: "delete",
  COPY: "copy",
  EDIT: "edit",
  SEND: "send",
};

export default {
  name: "CommsMessageDetails",
  components: {
    ConfirmationPopup: () => import("@/components/common/ConfirmationPopup"),
    CommsMessageRecipientsDetails: () =>
      import("@/components/communication/CommsMessageRecipientsDetails"),
  },
  props: {
    messageId: {
      type: [String, Number],
    },
    isDraft: {
      type: Boolean,
      default: true,
    },
    groups: {
      type: Array,
    },
    locations: {
      type: Array,
    },
  },
  data: () => ({
    confirmationShow: false,
    messageRecipients: null,
    loading: false,
    message: null,
    messageExpanded: false,
    isOverflowing: false,
    recipientDetailsUnlocked: false,
  }),
  mounted() {
    this.recipientDetailsUnlocked = this.featureUnlocked(
      "mass-comm-acknowledgement-details"
    );
  },
  computed: {
    messageDate() {
      if (
        this.message &&
        this.message.isDraft &&
        this.message.createdTimestamp
      ) {
        return lodashUpperFirst(
          getRelativeTimeFromNow(this.message.createdTimestamp)
        );
      }
      if (this.message && !this.message.isDraft && this.message.sentTimestamp) {
        return lodashUpperFirst(
          getRelativeTimeFromNow(this.message.sentTimestamp)
        );
      }
      return null;
    },
    messageBody() {
      if (this.message && this.message.body) {
        return this.message.body.replace(/[\n\r]/g, "<p/>");
      }
      return null;
    },
    btnVisibleEdit() {
      return this.message && this.message.isDraft;
    },
    btnDisabledCopy() {
      return !this.message;
    },
    btnDisabledDelete() {
      return !this.message;
    },
    btnVisibleSend() {
      return this.message && this.message.isDraft;
    },
    confirmationText() {
      if (lodashGet(this.message, "subject")) {
        return `Are you sure you want to delete the message <br/><strong>${this.message.subject}</strong>?`;
      }
      return null;
    },
    confirmationBtnCancel() {
      return {
        text: "Cancel",
        onClickFnc: async () => {
          this.setConfirmationShow(false);
        },
      };
    },
    confirmationBtnAction() {
      return {
        text: "Delete",
        onClickFnc: (params) => {
          this.setConfirmationShow(false);
          this.deleteMessage();
        },
      };
    },
  },
  methods: {
    async getMessage() {
      if (this.messageId) {
        this.message = await getMessage({
          messageId: this.messageId,
          noCache: this.isDraft,
        });
      }
    },
    setRecipients() {
      this.loading = true;
      if (this.message) {
        const grps = [];
        if (this.message.groups) {
          this.message.groups.forEach((msgGroup) => {
            const group = lodashFind(this.groups, [
              "groupId",
              msgGroup.groupId,
            ]);
            if (group) {
              grps.push(group.name);
            }
          });
        }

        if (this.message.locations) {
          this.message.locations.forEach((msgLocation) => {
            const location = lodashFind(this.locations, ["id", msgLocation.id]);
            if (location) {
              grps.push(location.name);
            }
          });
        }
        // https://stackoverflow.com/a/43015176
        this.messageRecipients = grps
          .join(", ")
          .replace(/, ((?:.(?!, ))+)$/, " and $1");
      } else {
        this.messageRecipients = "";
      }
      this.loading = false;
    },
    async deleteMessage() {
      await deleteMessage({ message: this.message });
      this.$emit(EMITTED_ACTIONS.DELETE);
    },
    async handleClickEdit() {
      if (this.message) {
        this.$emit(EMITTED_ACTIONS.EDIT, lodashCloneDeep(this.message));
        this.handlePageTrackerEvent(
          EVENT_CATEGORIES.mass_communication,
          EVENT_ACTIONS.clicked,
          "Edit a Message"
        );
      }
    },
    async handleClickCopy() {
      if (this.message) {
        const message = lodashCloneDeep(this.message);
        message.id = null;
        message.subject = `Copy of ${this.message.subject}`;
        delete message.messageId;
        delete message.sender;
        delete message.createdTimestamp;
        delete message.sentTimestamp;
        delete message.isDraft;
        this.$emit(EMITTED_ACTIONS.COPY, message);
        this.handlePageTrackerEvent(
          EVENT_CATEGORIES.mass_communication,
          EVENT_ACTIONS.clicked,
          "Copy a Message"
        );
      }
    },
    async handleClickSend() {
      if (this.message) {
        this.$emit(EMITTED_ACTIONS.SEND, this.message);
        this.handlePageTrackerEvent(
          EVENT_CATEGORIES.mass_communication,
          EVENT_ACTIONS.clicked,
          "Send a Message"
        );
      }
    },
    setConfirmationShow(value) {
      this.confirmationShow = value;
    },
    checkOverflowing() {
      var element = this.$refs.bodyDiv;
      this.isOverflowing =
        this.message && element && element.offsetHeight < element.scrollHeight;
    },
  },
  watch: {
    messageId: {
      async handler() {
        if (this.messageId) {
          this.messageExpanded = false;
          await this.getMessage();
          this.setRecipients();
          this.checkOverflowing();
        }
      },
      immediate: true,
    },
    groups: {
      handler() {
        this.setRecipients();
      },
    },
    locations: {
      handler() {
        this.setRecipients();
      },
    },
  },
  beforeDestroy() {
    this.confirmationShow = false;
  },
};
</script>

<style scoped>
.hyp-container {
  background-color: white;
}
.btn-icon {
  position: relative;
  top: 1px;
}
.message-body {
  max-height: 20vh;
  overflow-y: hidden;
}
.message-body.messageExpanded {
  max-height: none !important;
}
.message-container {
  overflow-y: auto;
  height: 70vh;
}
</style>
